import React, { useRef, useCallback, useEffect } from "react";

/**
 * Inline 0-AI logo. The red dot (and its inner shadow) live in a group
 * we can animate independently — it rolls off-screen left then back in
 * on page load and on click.
 */
export function Logo({ height = 20, className }: { height?: number; className?: string }) {
  const dotRef = useRef<SVGGElement>(null);

  const roll = useCallback(() => {
    const el = dotRef.current;
    if (!el) return;
    // Restart animation by removing and re-adding the class
    el.classList.remove("logo-dot-rolling");
    // Force reflow so the animation restarts even if the class was just removed
    void el.getBoundingClientRect();
    el.classList.add("logo-dot-rolling");
  }, []);

  // Allow other components to trigger the roll animation by dispatching
  // a "logo-dot-roll" event on window (e.g. burger menu toggle).
  useEffect(() => {
    window.addEventListener("logo-dot-roll", roll);
    return () => window.removeEventListener("logo-dot-roll", roll);
  }, [roll]);

  return (
    <svg
      width={(551 / 176) * height}
      height={height}
      viewBox="0 0 551 176"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onClick={roll}
      className={className}
      style={{ overflow: "visible", cursor: "pointer" }}
    >
      <defs>
        <mask id="logo-dot-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="170" height="176">
          <path
            d="M170 88C170 136.601 131.944 176 85 176C38.0558 176 0 136.601 0 88C0 39.3989 38.0558 0 85 0C131.944 0 170 39.3989 170 88Z"
            fill="#fff"
          />
        </mask>
      </defs>

      {/* Letterforms */}
      <path d="M259.5 0L361 176H158L259.5 0Z" fill="#FFFFFF" />
      <path d="M382 0H551V176H382V0Z" fill="#FFFFFF" />

      {/* The dot — animated as one unit. transform-origin is the dot's center
          (85, 88) in SVG userspace so rotation pivots correctly. */}
      <g
        ref={dotRef}
        className="logo-dot logo-dot-rolling"
        style={{ transformBox: "view-box", transformOrigin: "85px 88px" } as React.CSSProperties}
      >
        <path
          d="M170 88C170 136.601 131.944 176 85 176C38.0558 176 0 136.601 0 88C0 39.3989 38.0558 0 85 0C131.944 0 170 39.3989 170 88Z"
          fill="#FF3C00"
        />
        <g mask="url(#logo-dot-mask)">
          <rect x="-39" y="-18" width="124" height="233" fill="#962503" />
        </g>
      </g>
    </svg>
  );
}
